import type { Role } from '@takeshape/schema';
import { fromJS, type List } from 'immutable';
import get from 'lodash/get';
import omit from 'lodash/omit';
import { createSelector, type Selector } from 'reselect';
import type { AppState } from '../app-state.js';
import { getRouter } from '../selectors.js';
import { isApiKeyOnlyRole, isMeshOnlyRole, isUserOnlyRole } from './built-in-roles.js';
import { LIST_NAME } from './constants.js';
import type { UserRoleListState } from './list-reducer.js';

export const getRoleListState = (state: AppState): UserRoleListState => state[LIST_NAME];

export const getRoleId: Selector<AppState, string> = createSelector(getRouter, (router) =>
  get(router, ['params', 'roleId'], '')
);

export const getUserRoleList: Selector<AppState, Role[]> = createSelector(
  getRoleListState,
  (listState: UserRoleListState) => listState.data
);

export const getUserRoleListStatus: Selector<AppState, Omit<UserRoleListState, 'data'>> = createSelector(
  getRoleListState,
  (listState: UserRoleListState) => omit(listState, 'data')
);

export const getApiKeyRoles: Selector<AppState, Role[]> = createSelector(getUserRoleList, (roles: Role[]) =>
  roles.filter((r: Role) => !isUserOnlyRole(r))
);

export const getFormApiKeyRoleOptions: Selector<AppState, List<Map<'label' | 'value', string>>> = createSelector(
  getApiKeyRoles,
  (roles: Role[]) => {
    const options = roles?.map((role) => ({
      value: role.name,
      label: role.name
    }));
    return fromJS(options);
  }
);

export const getUserOnlyRoles: Selector<AppState, Role[]> = createSelector(getUserRoleList, (roles: Role[]) =>
  roles.filter((r: Role) => !isApiKeyOnlyRole(r) && !isMeshOnlyRole(r))
);

export const getCustomRoleCount: Selector<AppState, number> = createSelector(
  getUserRoleList,
  (roles: Role[]) => roles.filter((r: Role) => !r.builtIn).length
);
